import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  TrendingUp,
  Download,
  Calendar,
  Building,
  Users,
  DollarSign,
  BarChart3,
  PieChart,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  PieChart as RechartsPieChart,
  Cell,
  Pie,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  Area,
  AreaChart,
} from "recharts";

import { payrollAPI, departmentsAPI } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";

export default function PayrollReports() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const [filters, setFilters] = useState({
    type: "department",
    periode: new Date().toISOString().substring(0, 7), // YYYY-MM
    departement_id: "",
  });

  // Function to get the correct periode format based on type
  const getFormattedPeriode = () => {
    const currentDate = new Date();
    switch (filters.type) {
      case "monthly":
        return currentDate.toISOString().substring(0, 7); // YYYY-MM
      case "quarterly":
        const currentQuarter = Math.ceil((currentDate.getMonth() + 1) / 3);
        return `${currentDate.getFullYear()}-Q${currentQuarter}`; // YYYY-Q#
      case "yearly":
        return currentDate.getFullYear().toString(); // YYYY
      default:
        return currentDate.toISOString().substring(0, 7);
    }
  };

  // Update periode when type changes
  const handleTypeChange = (newType) => {
    const newPeriode = (() => {
      const currentDate = new Date();
      switch (newType) {
        case "monthly":
          return currentDate.toISOString().substring(0, 7); // YYYY-MM
        case "quarterly":
          const currentQuarter = Math.ceil((currentDate.getMonth() + 1) / 3);
          return `${currentDate.getFullYear()}-Q${currentQuarter}`; // YYYY-Q#
        case "yearly":
          return currentDate.getFullYear().toString(); // YYYY
        default:
          return currentDate.toISOString().substring(0, 7);
      }
    })();

    setFilters((prev) => ({
      ...prev,
      type: newType,
      periode: newPeriode,
    }));
  };

  // Chart colors
  const CHART_COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

  const reportTypes = [
    { value: "monthly", label: "Rapport mensuel" },
    { value: "quarterly", label: "Rapport trimestriel" },
    { value: "yearly", label: "Rapport annuel" },
    { value: "department", label: "Par département" },
  ];

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      const response = await departmentsAPI.getAll({ limit: 1000 });
      setDepartments(response.data?.data?.departments || []);
    } catch (error) {
      console.error("Error loading departments:", error);
      setDepartments([]); // Ensure departments is always an array
      toast.error("Erreur lors du chargement des départements");
    }
  };

  const generateReport = async () => {
    if (!filters.type || !filters.periode) {
      toast.error("Veuillez sélectionner le type de rapport et la période");
      return;
    }

    try {
      setLoading(true);
      const params = {
        type: filters.type,
        periode: filters.periode,
        ...(filters.departement_id && {
          departement_id: filters.departement_id,
        }),
      };

      const response = await payrollAPI.getReports(params);

      if (response.data.success) {
        setReportData(response.data.data);
        toast.success("Rapport généré avec succès");
      } else {
        toast.error(
          response.data.message || "Erreur lors de la génération du rapport"
        );
      }
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("Erreur lors de la génération du rapport");
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format = "csv") => {
    if (!reportData || reportData.length === 0) {
      toast.error("Aucun rapport à exporter");
      return;
    }

    try {
      setLoading(true); // Use setLoading for consistency

      if (format === "csv") {
        // Generate CSV with Moroccan payroll fields
        const headers = [
          "Employé",
          "Département",
          "Période",
          "Salaire Base",
          "Heures Travaillées",
          "Heures Supplémentaires",
          "Primes",
          "Salaire Brut",
          "CNSS (5.5%)",
          "AMO (2%)",
          "Retraite (1%)",
          "Total Cotisations",
          "Frais Professionnels",
          "Autres Déductions",
          "Salaire Net Imposable",
          "IR Brut",
          "Déduction Personnes à Charge",
          "IR Net",
          "Salaire Net Final",
          "Statut",
        ];

        const csvData = reportData.map((item) => [
          `${item.employe?.prenom} ${item.employe?.nom}`,
          item.employe?.departement?.nom || "",
          `${new Date(item.periode_debut).toLocaleDateString(
            "fr-MA"
          )} - ${new Date(item.periode_fin).toLocaleDateString("fr-MA")}`,
          parseFloat(item.salaire_base || 0).toFixed(2),
          parseFloat(item.heures_travaillees || 151.67).toFixed(2),
          parseFloat(item.heures_supplementaires || 0).toFixed(2),
          parseFloat(item.primes || 0).toFixed(2),
          parseFloat(item.salaire_brut || 0).toFixed(2),
          parseFloat(item.cotisations_cnss || 0).toFixed(2),
          parseFloat(item.cotisations_amo || 0).toFixed(2),
          parseFloat(item.cotisations_retraite || 0).toFixed(2),
          parseFloat(item.cotisations_sociales || 0).toFixed(2),
          parseFloat(item.frais_professionnels || 0).toFixed(2),
          parseFloat(item.autres_deductions || 0).toFixed(2),
          parseFloat(item.salaire_net_imposable || 0).toFixed(2),
          parseFloat(item.ir_brut || 0).toFixed(2),
          parseFloat(item.deduction_personnes || 0).toFixed(2),
          parseFloat(item.ir_net || 0).toFixed(2),
          parseFloat(item.salaire_net || 0).toFixed(2),
          item.statut || "",
        ]);

        const csvContent = [headers, ...csvData]
          .map((row) => row.map((cell) => `"${cell}"`).join(","))
          .join("\n");

        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `rapport_paie_marocain_${filters.type}_${filters.periode}.csv`
        );
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success("Rapport exporté en CSV avec succès");
      }
    } catch (error) {
      console.error("Error exporting report:", error);
      toast.error("Erreur lors de l'export du rapport");
    } finally {
      setLoading(false);
    }
  };

  const canViewReports = user?.role === "admin" || user?.role === "rh";

  if (!canViewReports) {
    return (
      <div className="text-center py-8">
        <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Accès non autorisé</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <TrendingUp className="h-8 w-8 text-blue-600" />
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Rapports de Paie
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Générez et consultez des rapports détaillés sur la paie
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtres</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="reportType">Type de rapport</Label>
              <Select value={filters.type} onValueChange={handleTypeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner le type" />
                </SelectTrigger>
                <SelectContent>
                  {reportTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="periode">Période</Label>
              {filters.type === "quarterly" ? (
                <Select
                  value={filters.periode}
                  onValueChange={(value) =>
                    setFilters((prev) => ({ ...prev, periode: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un trimestre" />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const quarters = [];
                      const currentYear = new Date().getFullYear();
                      // Generate quarters for current and previous year
                      [currentYear - 1, currentYear].forEach((year) => {
                        for (let q = 1; q <= 4; q++) {
                          quarters.push(
                            <SelectItem
                              key={`${year}-Q${q}`}
                              value={`${year}-Q${q}`}
                            >
                              {year} - T{q}
                            </SelectItem>
                          );
                        }
                      });
                      return quarters;
                    })()}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="periode"
                  type={filters.type === "yearly" ? "number" : "month"}
                  value={filters.periode}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, periode: e.target.value }))
                  }
                  placeholder={
                    filters.type === "yearly"
                      ? "YYYY"
                      : filters.type === "monthly"
                        ? "YYYY-MM"
                        : "Période"
                  }
                  min={filters.type === "yearly" ? "2020" : undefined}
                  max={filters.type === "yearly" ? "2030" : undefined}
                />
              )}
            </div>

            {filters.type === "department" && (
              <div>
                <Label htmlFor="department">Départements</Label>
                <Select
                  value={filters.departement_id || "all"}
                  onValueChange={(value) =>
                    setFilters((prev) => ({
                      ...prev,
                      departement_id: value === "all" ? "" : value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un département" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    {Array.isArray(departments) &&
                      departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>
                          {dept.nom}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-end">
              <Button
                onClick={generateReport}
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Chargement...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <BarChart3 className="h-4 w-4" />
                    <span>Générer le rapport</span>
                  </div>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {reportData && (
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="detailed">Détaillé</TabsTrigger>
            <TabsTrigger value="charts">Graphiques</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Users className="h-8 w-8 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">
                        Total employés
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {reportData.totalEmployees || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <DollarSign className="h-8 w-8 text-green-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">
                        Salaire brut total
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {(reportData.totalGrossSalary || 0).toLocaleString(
                          "fr-MA",
                          {
                            style: "currency",
                            currency: "MAD",
                          }
                        )}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <DollarSign className="h-8 w-8 text-purple-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">
                        Salaire net total
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {(reportData.totalNetSalary || 0).toLocaleString(
                          "fr-MA",
                          {
                            style: "currency",
                            currency: "MAD",
                          }
                        )}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Calendar className="h-8 w-8 text-orange-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">
                        Total heures sup.
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {reportData.totalOvertimeHours || 0}h
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Moyennes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span>Salaire brut moyen</span>
                    <span className="font-medium">
                      {(reportData.averageGrossSalary || 0).toLocaleString(
                        "fr-MA",
                        {
                          style: "currency",
                          currency: "MAD",
                        }
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Salaire net moyen</span>
                    <span className="font-medium">
                      {(reportData.averageNetSalary || 0).toLocaleString(
                        "fr-MA",
                        {
                          style: "currency",
                          currency: "MAD",
                        }
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Heures sup. moyennes</span>
                    <span className="font-medium">
                      {(reportData.averageOvertimeHours || 0).toFixed(1)}h
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    Exporter
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => exportReport("csv")}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        CSV
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => exportReport("excel")}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Excel
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">
                    Exportez ce rapport au format CSV ou Excel
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Detailed Tab */}
          <TabsContent value="detailed">
            <Card>
              <CardHeader>
                <CardTitle>{t("payroll.reports.detailedBreakdown")}</CardTitle>
              </CardHeader>
              <CardContent>
                {reportData.departmentBreakdown && (
                  <div className="space-y-6">
                    <h4 className="font-medium">
                      {t("payroll.reports.byDepartment")}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {reportData.departmentBreakdown.map((dept, index) => (
                        <Card key={index}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-medium">{dept.name}</h5>
                              <Building className="h-4 w-4 text-gray-400" />
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span>Employés</span>
                                <span>{dept.employeeCount}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>{t("payroll.totalCost")}</span>
                                <span className="font-medium">
                                  {dept.totalCost.toLocaleString("fr-MA", {
                                    style: "currency",
                                    currency: "MAD",
                                  })}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>{t("payroll.averageSalary")}</span>
                                <span>
                                  {dept.averageSalary.toLocaleString("fr-MA", {
                                    style: "currency",
                                    currency: "MAD",
                                  })}
                                </span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {reportData.payslips && (
                  <div className="mt-8">
                    <h4 className="font-medium mb-4">
                      {t("payroll.reports.individualPayslips")}
                    </h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t("employees.employee")}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Département
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t("payroll.grossSalary")}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t("payroll.netSalary")}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t("payroll.overtimeHours")}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {reportData.payslips.map((payslip, index) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {payslip.employe?.prenom}{" "}
                                    {payslip.employe?.nom}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {payslip.employe?.email}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {payslip.employe?.departement?.nom}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {parseFloat(
                                  payslip.salaire_brut || 0
                                ).toLocaleString("fr-MA", {
                                  style: "currency",
                                  currency: "MAD",
                                })}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {parseFloat(
                                  payslip.salaire_net || 0
                                ).toLocaleString("fr-MA", {
                                  style: "currency",
                                  currency: "MAD",
                                })}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {payslip.heures_supplementaires || 0}h
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Charts Tab */}
          <TabsContent value="charts">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <PieChart className="h-5 w-5" />
                    <span>Répartition des salaires</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    {reportData?.salaryDistribution ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={reportData.salaryDistribution}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ label, percent }) =>
                              `${label} (${(percent * 100).toFixed(0)}%)`
                            }
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="count"
                            nameKey="label"
                          >
                            {reportData.salaryDistribution.map(
                              (entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={
                                    CHART_COLORS[index % CHART_COLORS.length]
                                  }
                                />
                              )
                            )}
                          </Pie>
                          <Tooltip
                            formatter={(value, name) => [value, "Employés"]}
                          />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
                        <p className="text-gray-500">
                          Générez un rapport pour voir les données
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <BarChart3 className="h-5 w-5" />
                    <span>Comparaison départements</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    {reportData?.departmentBreakdown ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={reportData.departmentBreakdown}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="name"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            fontSize={12}
                          />
                          <YAxis />
                          <Tooltip
                            formatter={(value, name) => [
                              name === "totalCost"
                                ? `${value.toLocaleString("fr-MA")} MAD`
                                : value,
                              name === "totalCost" ? "Coût total" : "Employés",
                            ]}
                          />
                          <Legend />
                          <Bar
                            dataKey="employeeCount"
                            fill="#8884d8"
                            name="Employés"
                          />
                          <Bar
                            dataKey="totalCost"
                            fill="#82ca9d"
                            name="Coût total (MAD)"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
                        <p className="text-gray-500">
                          Générez un rapport pour voir les données
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <TrendingUp className="h-5 w-5" />
                    <span>Tendances</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    {reportData?.trends ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={reportData.trends}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="period" />
                          <YAxis />
                          <Tooltip
                            formatter={(value, name) => [
                              typeof value === "number" &&
                                name.includes("total")
                                ? `${value.toLocaleString("fr-MA")} MAD`
                                : value,
                              name === "totalGross"
                                ? "Salaire brut total"
                                : name === "totalNet"
                                  ? "Salaire net total"
                                  : "Employés",
                            ]}
                          />
                          <Legend />
                          <Area
                            type="monotone"
                            dataKey="totalGross"
                            stackId="1"
                            stroke="#8884d8"
                            fill="#8884d8"
                            name="Salaire brut"
                          />
                          <Area
                            type="monotone"
                            dataKey="totalNet"
                            stackId="1"
                            stroke="#82ca9d"
                            fill="#82ca9d"
                            name="Salaire net"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
                        <p className="text-gray-500">
                          Générez un rapport pour voir les données
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* Empty State */}
      {!reportData && (
        <Card>
          <CardContent className="text-center py-12">
            <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucune donnée de rapport
            </h3>
            <p className="text-gray-600">
              Générez d'abord un rapport pour voir les données
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
